import { LitElement, html, ifDefined, styles, observeState, throttler, defineElement } from '@mds/core';
import { navigationLinkCSS } from './navigation-link.styles.js';

import { navigationState } from './navigation-state.js';

const isKeyboardClickEvent = (/** @type {KeyboardEvent} */ e) => e.key === ' ' || e.key === 'Enter';
const isSpaceKeyboardClickEvent = (/** @type {KeyboardEvent} */ e) => e.key === ' ';
const isArrowUpKeyboardEvent = (/** @type {KeyboardEvent} */ e) => e.key === 'ArrowUp';
const isArrowDownKeyboardEvent = (/** @type {KeyboardEvent} */ e) => e.key === 'ArrowDown';

/**
 * Navigation Web Component
 */
export class MdsNavigationLink extends observeState(LitElement) {

	static get styles() {
		return [...styles, navigationLinkCSS];
	}

	static get properties() {
		return {
			href: { type: String, reflect: true },
			text: { type: String, reflect: true },
			title: { type: String, reflect: true },
			type: { type: String, reflect: true },
			icon: { type: String, reflect: true },
			style: { type: String, reflect: true },
			tabindex: { type: String, reflect: true },
			break: { type: Boolean, reflect: true },
			current: { type: Boolean, reflect: true },
			active: { type: Boolean, reflect: true },
			toggled: { type: Boolean, reflect: true },
			hasdropdown: { type: Boolean, reflect: true },
			issubmenulink: { type: Boolean, reflect: true }

		};
	}

	constructor() {
		super();
		this.href = undefined;
		this.text = undefined;
		this.title = undefined;
		this.type = undefined;
		this.break = false;
		this.icon = undefined;
		this.style = undefined;
		this.tabindex = undefined;
		this.active = false;
		this.focus = false;
		this.current = false;
		this.toggled = false;
		this.hasdropdown = false;
		this.issubmenulink = false;
		this.addEventListener('keydown', this.__keydownHandler);
		this.addEventListener('keyup', this.__keyupHandler);
		window.addEventListener(
			'resize',
			throttler(() => this.__toggle(false)),
			{ passive: true }
		);
		document.body.addEventListener('click',(event) => {
			if (event.target !== this.shadowRoot.host) this.__toggle(false);
		});

	}
	firstUpdated(changedProperties) {
		this.dropdown = this.shadowRoot.host.querySelector('mds-dropdown');
		this.hasdropdown = !!this.dropdown;
		// the first parent is the slot, so we go up to the grandparent.
		this.issubmenulink = !!this.shadowRoot.host.closest('mds-dropdown');
		this.topLevel = this.shadowRoot.host.hasAttribute('icon');
		this.dispatchClick = new Event("click", { "bubbles":true, "cancelable":true });
		this.toggledElement = this.shadowRoot.host.closest(this.tagName);
		this.tabindex = "0";
		this.__setTabIndex();
	}

	__setTabIndex(){
		Array.from(this.querySelectorAll(this.tagName)).forEach(el => {
			requestAnimationFrame(()=>{
				el.setAttribute('tabindex', this.toggled ? '0' : '-1');
			});
		});
	}

	__arrowNavigation(event) {

		const close = () => {
			if (this.issubmenulink) {
				const navLink = this.shadowRoot.host.closest('mds-dropdown').closest(this.tagName.toLowerCase());
				navLink.shadowRoot.querySelector('a').click()
				navLink.dispatchEvent(this.dispatchClick);
			}
			this.__setTabIndex();
		}

		/* focus first item */
		if (event.target.hasAttribute('hasdropdown') && !this.toggled) {
			if (isArrowDownKeyboardEvent(event)) {
				this.__clickEvent();
			}
		} else if (event.target.hasAttribute('hasdropdown') && this.toggled) {
			if (isArrowDownKeyboardEvent(event) || isArrowUpKeyboardEvent(event)) {
				const link = this.dropdown.shadowRoot.host.querySelector(this.tagName.toLowerCase());
				const trigger = link && link.shadowRoot.querySelector('a');
				if (typeof trigger?.focus === 'function') trigger.focus();
				this.__toggle(true);
			}
		} else {
			if (this.issubmenulink) {
				const anchors = new Array();
				const dropdown = event.target.closest('mds-dropdown');
				const navLinks = [...dropdown.querySelectorAll(this.tagName.toLowerCase())];
				navLinks.forEach(sl => {
					anchors.push(sl.shadowRoot.querySelector('a'));
				});
				const thisIndex = navLinks.indexOf(event.target);
				if (isArrowDownKeyboardEvent(event)) {
					this.__toggle(true);
					((thisIndex + 1) < anchors.length)
						? anchors[thisIndex + 1].focus()
						: close();
				}

				if (isArrowUpKeyboardEvent(event)) {
					this.__toggle(true);
					((thisIndex - 1) > -1)
						? anchors[thisIndex - 1].focus()
						: close();
				}
			}
		}
	}

	render() {
		this.break = navigationState.break ;
		return this.__template();
	}

	__toggle(toggle = false) {
		this.toggled = toggle;
	}

	__clickEvent(event) {
		if(!this.toggled && this.topLevel && !this.issubmenulink) document.body.dispatchEvent(this.dispatchClick);
		if(this.href) this.shadowRoot.querySelector('a[href]').click();
		this.__toggle(!this.toggled);
		this.__setTabIndex()
	}

	/**
	 * Navigation Template
	 * @private
	 */
	__template() {
		this.active = this.current;
		if (this.href ||  this.issubmenulink) {
			this.href = encodeURI(this.href);
		}
		this.style = this.icon && `--icon-glyph: var(--mds-icon-glyphs-base-${this.icon.toLowerCase()})`;
		return html`
			<a
				title="${ifDefined(this.title)}"
				?active="${this.active}"
				href="${ifDefined(this.href)}"
				style="${ifDefined(this.style)}"
				tabindex='-1'
				@click="${this.__clickEvent.bind(this)}"
			>
				<span><slot name="link-content">${this.text}</slot></span>
			</a>
			<slot name="submenu"></slot>
		`;
	}


	/**
	 * @param {KeyboardEvent} event
	 * @private
	 */
	__keydownHandler(event) {

		if (isArrowDownKeyboardEvent(event) || isArrowUpKeyboardEvent(event)) {
			this.__arrowNavigation(event);
		}

		if (this.focus || !isKeyboardClickEvent(event)) {
			if (isSpaceKeyboardClickEvent(event)) {
				event.preventDefault();
			}
			return;
		}

		if (isSpaceKeyboardClickEvent(event)) {
			event.preventDefault();
		}
		this.focus = true;
		/**
		 * @param {KeyboardEvent} keyupEvent
		 */
		const keyupHandler = (keyupEvent) => {
			if (isKeyboardClickEvent(keyupEvent)) {
				this.focus = false;
				document.removeEventListener('keyup', keyupHandler, true);
			}
		};
		document.addEventListener('keyup', keyupHandler, true);
	}

	/**
	 * @param {KeyboardEvent} event
	 * @private
	 */
	__keyupHandler(event) {
		if (isArrowDownKeyboardEvent(event) || isArrowUpKeyboardEvent(event)) {
			event.preventDefault();
		}

		if (isKeyboardClickEvent(event)) {
			// Fixes IE11 double submit/click. Enter keypress somehow triggers the __keyUpHandler on the native <button>
			if (event.target && event.target !== this) {
				return;
			}
			// dispatch click
			this.__clickEvent();
		}
	}

}

defineElement('mds-navigation-link', MdsNavigationLink);
